import {
  WebSocketReadyState,
  WebSocketService,
  createWebSocket,
} from '@/services/websocketService'
import store from '@/redux/store'
import {
  setConnectionStatus as setAdvisorConnectionStatus,
  setWebSocketError as setAdvisorWebSocketError,
} from '@/redux/slices/advisorWebsocketSlice'
import {
  setConnected as setCharterPrepConnected,
  setError as setCharterPrepError,
} from '@/features/charter/redux/slices/charterPrepWsSlice'
import {
  setGroupConnected as setCharterGroupConnected,
  setGroupError as setCharterGroupError,
} from '@/features/charter/redux/slices/charterGroupWsSlice'
import { setConnectionStatus as setNegotiationConnectionStatus } from '@/redux/slices/negotiationWebsocketSlice'
import { setConnectionStatus as setTeamChatConnectionStatus } from '@/redux/slices/teamChatSlice'
import { setConnectionStatus as setDecisionConnectionStatus } from '@/redux/slices/studentDecisionWebsocketSlice'
import {
  setConnectionStatus as setAIPlayerConnectionStatus,
  setError as setAIPlayerError,
} from '@/redux/slices/aiPlayerChatSlice'
import {
  setConnectionStatus as setWaitingRoomConnectionStatus,
  setWebSocketError as setWaitingRoomWebSocketError,
} from '@/redux/slices/waitingRoomWebsocketSlice'
import {
  setConnectionStatus as setAdjudicationConnectionStatus,
  setError as setAdjudicationError,
} from '@/redux/slices/adjudicationMessageSlice'
import {
  setConnectionStatus as setTimerConnectionStatus,
  setError as setTimerError,
} from '@/redux/slices/timerWebsocketSlice'
import {
  setConnectionStatus as setTestChatConnectionStatus,
  setError as setTestChatError,
} from '@/redux/slices/testChatSlice'
import {
  setAIPlayerTestChatConnectionStatus,
  setAIPlayerTestChatError,
} from '@/redux/slices/aiPlayerTestChatSlice'
import {
  WebSocketEndpoint,
  MessageProcessor,
} from '@/services/websocketProcessorTypes'
import {
  advisorProcessors,
  negotiationProcessors,
  studentDecisionProcessors,
  adjudicationProcessors,
  waitingRoomProcessors,
  timerProcessors,
  notificationProcessors,
  teamChatProcessors,
  aiPlayerProcessors,
} from '@/services/websocketProcessors'
import { testChatProcessors } from '@/services/websocketProcessors/testChatProcessors'
import { aiPlayerTestChatProcessors } from '@/services/websocketProcessors/aiPlayerTestChatProcessors'
import {
  charterPrepProcessors,
  charterGroupProcessors,
  buildCharterPrepMonitorProcessors,
} from '@/features/charter/services/charterWsProcessors'
import { setMonitorConnected } from '@/features/charter/redux/slices/charterPrepMonitorSlice'
import {
  setNotificationConnectionStatus,
  setNotificationWebSocketError,
} from '@/redux/slices/notificationSlice'
import {
  setError as setGameAnalysisError,
  setConnectionStatus as setGameAnalysisConnectionStatus,
} from '@/redux/slices/gameAnalysisSlice'
import { gameAnalysisProcessors } from '@/services/websocketProcessors/gameAnalysisProcessor'
import { leaveRoom } from '@/utils/roomAccess'

class WebSocketManager {
  private static instance: WebSocketManager
  private connections: Map<string, WebSocketService> = new Map()
  private messageProcessors: Map<string, MessageProcessor[]> = new Map()
  private connectionHandlers: Map<
    string,
    {
      onConnected: () => void
      onDisconnected: () => void
      onError: (error: Error) => void
    }
  > = new Map()

  private constructor() {
    this.initializeMessageProcessors()
  }

  public static getInstance(): WebSocketManager {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager()
    }
    return WebSocketManager.instance
  }

  private initializeMessageProcessors() {
    this.messageProcessors.set('advisor', advisorProcessors)
    this.messageProcessors.set('negotiation', negotiationProcessors)
    this.messageProcessors.set('waitingRoom', waitingRoomProcessors)
    this.messageProcessors.set('studentDecision', studentDecisionProcessors)
    this.messageProcessors.set('adjudication', adjudicationProcessors)
    this.messageProcessors.set('timer', timerProcessors)
    this.messageProcessors.set('testChat', testChatProcessors)
    this.messageProcessors.set('aiPlayerTestChat', aiPlayerTestChatProcessors)
    this.messageProcessors.set('notifications', notificationProcessors)
    this.messageProcessors.set('gameAnalysis', gameAnalysisProcessors)
    this.messageProcessors.set('team_chat', teamChatProcessors)
    this.messageProcessors.set('ai_player_chat', aiPlayerProcessors)
    this.messageProcessors.set('charter_prep', charterPrepProcessors)
    this.messageProcessors.set('charter_group', charterGroupProcessors)
  }

  private handleMessage(connectionId: string, event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data)

      // Keepalive frames — ignore silently across all connections.
      if (data.type === 'heartbeat' || data.type === 'pong') {
        return
      }

      if (data.type === 'removed_from_room') {
        leaveRoom("You've been removed from this game.")
        return
      }

      const processors = this.messageProcessors.get(connectionId)

      if (processors) {
        if (data.type) {
          const processor = processors.find((p) => p.type === data.type)
          if (processor) {
            processor.handler(data)
            return
          }
        }

        if (data.status !== undefined && connectionId === 'waitingRoom') {
          const processor = processors.find((p) => p.type === 'status')
          if (processor) {
            processor.handler(data)
            return
          }
        }
      }

      console.warn(`Unhandled WebSocket message for ${connectionId}:`, data)
    } catch (err) {
      console.error(`Error processing ${connectionId} WebSocket message:`, err)

      switch (connectionId) {
        case 'advisor':
          store.dispatch(setAdvisorWebSocketError('Error processing message'))
          break
        case 'adjudication':
          store.dispatch(setAdjudicationError('Error processing message'))
          break
        case 'waitingRoom':
          store.dispatch(
            setWaitingRoomWebSocketError('Error processing message')
          )
          break
        case 'timer':
          store.dispatch(setTimerError('Error processing message'))
          break
        case 'notifications':
          store.dispatch(
            setNotificationWebSocketError('Error processing message')
          )
          break
      }
    }
  }

  public connectToAdvisorChat(
    jwtToken: string,
    inviteCode: string,
    phaseId?: string,
    studentId?: string,
    impersonatePlayerId?: string
  ): Promise<void> {
    const connectionId = 'advisor'

    const params: Record<string, any> = {
      jwt_key: jwtToken,
      invite_code: inviteCode,
    }

    if (studentId) {
      params.student_id = studentId
    }

    if (phaseId) {
      params.phase_id = phaseId
    }

    if (impersonatePlayerId) {
      params.impersonate_player_id = impersonatePlayerId
    }

    const websocket = createWebSocket(WebSocketEndpoint.ADVISOR, params, {
      onOpen: () => {
        store.dispatch(setAdvisorConnectionStatus(true))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onConnected) {
          handler.onConnected()
        }
      },
      onMessage: (event) => this.handleMessage(connectionId, event),
      onError: (error) => {
        console.error('Advisor WebSocket error:', error)
        store.dispatch(setAdvisorConnectionStatus(false))
        store.dispatch(setAdvisorWebSocketError('WebSocket error'))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onError) {
          handler.onError(new Error('Advisor WebSocket error'))
        }
      },
      onClose: () => {
        store.dispatch(setAdvisorConnectionStatus(false))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onDisconnected) {
          handler.onDisconnected()
        }
      },
      reconnectInterval: 2000,
      maxReconnectAttempts: 5,
      debug: true,
    })

    this.connections.set(connectionId, websocket)

    return websocket.connect()
  }

  public connectToAdvisorMonitoring(
    jwtToken: string,
    inviteCode: string,
    studentId: string
  ): Promise<void> {
    const connectionId = `advisor_monitor_${studentId}`

    const existing = this.connections.get(connectionId)
    if (existing?.getReadyState() === WebSocketReadyState.OPEN) {
      return Promise.resolve()
    }

    const params = {
      jwt_key: jwtToken,
      invite_code: inviteCode,
      student_id: studentId,
    }

    const websocket = createWebSocket(WebSocketEndpoint.ADVISOR, params, {
      onOpen: () => {},
      onMessage: (event) => this.handleMessage(connectionId, event),
      onError: () => {},
      onClose: () => {},
      reconnectInterval: 5000,
      maxReconnectAttempts: 3,
    })

    this.connections.set(connectionId, websocket)
    this.messageProcessors.set(connectionId, advisorProcessors)

    return websocket.connect()
  }

  public async connectToNotification(jwtToken: string): Promise<void> {
    const connectionId = 'notifications'

    const existingConnection = this.connections.get(connectionId)

    if (existingConnection) {
      const readyState = existingConnection.getReadyState()

      if (readyState === WebSocketReadyState.OPEN) {
        console.log('Notification WebSocket already connected')
        return Promise.resolve()
      }

      if (readyState === WebSocketReadyState.CONNECTING) {
        console.log('Notification WebSocket is connecting, waiting...')
        await new Promise((resolve) => setTimeout(resolve, 1000))
      }

      await existingConnection.disconnect()
      this.connections.delete(connectionId)
    }

    const params: Record<string, any> = {
      jwt_key: jwtToken,
    }

    const websocket = createWebSocket(WebSocketEndpoint.NOTIFICATION, params, {
      onOpen: () => {
        store.dispatch(setNotificationConnectionStatus(true))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onConnected) {
          handler.onConnected()
        }
      },
      onMessage: (event) => this.handleMessage(connectionId, event),
      onError: (error) => {
        console.error('Notification WebSocket error:', error)
        store.dispatch(setNotificationConnectionStatus(false))
        store.dispatch(setNotificationWebSocketError('WebSocket error'))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onError) {
          handler.onError(new Error('Notification WebSocket error'))
        }
      },
      onClose: () => {
        store.dispatch(setNotificationConnectionStatus(false))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onDisconnected) {
          handler.onDisconnected()
        }
      },
      reconnectInterval: 2000,
      maxReconnectAttempts: 5,
      debug: true,
    })

    this.connections.set(connectionId, websocket)

    return websocket.connect()
  }

  public connectToNegotiation(
    jwtToken: string,
    roomId: string,
    negotiationId?: string,
    phaseId?: string,
    roundId?: string,
    impersonatePlayerId?: string
  ): Promise<void> {
    const connectionId = 'negotiation'

    const path = `/${roomId}/${WebSocketEndpoint.NEGOTIATION}`

    const params: Record<string, string> = {
      jwt_key: jwtToken,
      negotiation_id: negotiationId || '',
      phase_id: phaseId || '',
      round_id: roundId || '',
    }

    if (impersonatePlayerId) {
      params.impersonate_player_id = impersonatePlayerId
    }

    const websocket = createWebSocket(path, params, {
      onOpen: () => {
        store.dispatch(setNegotiationConnectionStatus(true))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onConnected) {
          handler.onConnected()
        }
      },
      onMessage: (event) => this.handleMessage(connectionId, event),
      onError: (error) => {
        console.error('Negotiation WebSocket error:', error)
        store.dispatch(setNegotiationConnectionStatus(false))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onError) {
          handler.onError(new Error('Negotiation WebSocket error'))
        }
      },
      onClose: () => {
        store.dispatch(setNegotiationConnectionStatus(false))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onDisconnected) {
          handler.onDisconnected()
        }
      },
      reconnectInterval: 2000,
      maxReconnectAttempts: 5,
      debug: true,
    })

    this.connections.set(connectionId, websocket)

    return websocket.connect()
  }

  public connectToTeamChat(
    jwtToken: string,
    roomId: string,
    teamId?: string,
    phaseId?: string,
    impersonatePlayerId?: string
  ): Promise<void> {
    const connectionId = 'team_chat'

    const path = `/${roomId}/${WebSocketEndpoint.TEAM_CHAT}`

    const params: Record<string, string> = {
      jwt_key: jwtToken,
      team_id: teamId || '',
      phase_id: phaseId || '',
    }

    if (impersonatePlayerId) {
      params.impersonate_player_id = impersonatePlayerId
    }

    const websocket = createWebSocket(path, params, {
      onOpen: () => {
        store.dispatch(setTeamChatConnectionStatus(true))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onConnected) {
          handler.onConnected()
        }
      },
      onMessage: (event) => this.handleMessage(connectionId, event),
      onError: (error) => {
        console.error('Team Chat WebSocket error:', error)
        store.dispatch(setTeamChatConnectionStatus(false))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onError) {
          handler.onError(new Error('Team Chat WebSocket error'))
        }
      },
      onClose: () => {
        store.dispatch(setTeamChatConnectionStatus(false))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onDisconnected) {
          handler.onDisconnected()
        }
      },
      reconnectInterval: 2000,
      maxReconnectAttempts: 5,
      debug: true,
    })

    this.connections.set(connectionId, websocket)

    return websocket.connect()
  }

  public connectToAIPlayerChat(
    jwtToken: string,
    roomId: string,
    teamId?: string
  ): Promise<void> {
    const connectionId = 'ai_player_chat'
    const path = `/${roomId}/${WebSocketEndpoint.AI_PLAYER_CHAT}`
    const params: Record<string, string> = {
      jwt_key: jwtToken,
      team_id: teamId || '',
    }

    const websocket = createWebSocket(path, params, {
      onOpen: () => {
        store.dispatch(setAIPlayerConnectionStatus(true))
      },
      onMessage: (event) => this.handleMessage(connectionId, event),
      onError: () => {
        store.dispatch(setAIPlayerConnectionStatus(false))
        store.dispatch(setAIPlayerError('AI Player WebSocket error'))
      },
      onClose: () => {
        store.dispatch(setAIPlayerConnectionStatus(false))
      },
      reconnectInterval: 2000,
      maxReconnectAttempts: 5,
      debug: true,
    })

    this.connections.set(connectionId, websocket)
    return websocket.connect()
  }

  public connectToStudentDecision(
    jwtToken: string,
    roomId: string,
    phaseId?: string,
    impersonatePlayerId?: string
  ): Promise<void> {
    const connectionId = 'studentDecision'

    const path = `/${roomId}/${WebSocketEndpoint.STUDENT_DECISION}`

    const params: Record<string, string> = {
      jwt_key: jwtToken,
      phase_id: phaseId || '',
    }

    if (impersonatePlayerId) {
      params.impersonate_player_id = impersonatePlayerId
    }

    const websocket = createWebSocket(path, params, {
      onOpen: () => {
        store.dispatch(setDecisionConnectionStatus(true))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onConnected) {
          handler.onConnected()
        }
      },
      onMessage: (event) => this.handleMessage(connectionId, event),
      onError: (error) => {
        console.error('Student Decision WebSocket error:', error)
        store.dispatch(setDecisionConnectionStatus(false))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onError) {
          handler.onError(new Error('Student Decision WebSocket error'))
        }
      },
      onClose: () => {
        store.dispatch(setDecisionConnectionStatus(false))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onDisconnected) {
          handler.onDisconnected()
        }
      },
      reconnectInterval: 2000,
      maxReconnectAttempts: 5,
      debug: true,
    })

    this.connections.set(connectionId, websocket)

    return websocket.connect()
  }

  public connectToWaitingRoom(
    jwtToken: string,
    roomId: string,
    inviteCode: string
  ): Promise<void> {
    const connectionId = 'waitingRoom'

    const path = `/${roomId}/${WebSocketEndpoint.WAITING_ROOM}`

    const params: Record<string, string> = {
      jwt_key: jwtToken,
      invite_code: inviteCode,
    }

    const websocket = createWebSocket(path, params, {
      onOpen: () => {
        store.dispatch(setWaitingRoomConnectionStatus(true))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onConnected) {
          handler.onConnected()
        }
      },
      onMessage: (event) => this.handleMessage(connectionId, event),
      onError: (error) => {
        console.error('Waiting Room WebSocket error:', error)
        store.dispatch(setWaitingRoomConnectionStatus(false))
        store.dispatch(setWaitingRoomWebSocketError('WebSocket error'))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onError) {
          handler.onError(new Error('Waiting Room WebSocket error'))
        }
      },
      onClose: () => {
        store.dispatch(setWaitingRoomConnectionStatus(false))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onDisconnected) {
          handler.onDisconnected()
        }
      },
      reconnectInterval: 2000,
      maxReconnectAttempts: 5,
      debug: true,
    })

    this.connections.set(connectionId, websocket)

    return websocket.connect()
  }

  public connectToAdjudication(
    jwtToken: string,
    decisionId: string,
    phaseId?: string
  ): Promise<void> {
    const connectionId = 'adjudication'

    const path = `${WebSocketEndpoint.ADJUDICATION}${decisionId}/chat/`

    const params: Record<string, string> = {
      jwt_key: jwtToken,
      phase_id: phaseId || '',
    }

    const websocket = createWebSocket(path, params, {
      onOpen: () => {
        store.dispatch(setAdjudicationConnectionStatus(true))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onConnected) {
          handler.onConnected()
        }
      },
      onMessage: (event) => this.handleMessage(connectionId, event),
      onError: (error) => {
        console.error('Adjudication WebSocket error:', error)
        store.dispatch(setAdjudicationConnectionStatus(false))
        store.dispatch(setAdjudicationError('WebSocket error'))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onError) {
          handler.onError(new Error('Adjudication WebSocket error'))
        }
      },
      onClose: () => {
        store.dispatch(setAdjudicationConnectionStatus(false))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onDisconnected) {
          handler.onDisconnected()
        }
      },
      reconnectInterval: 2000,
      maxReconnectAttempts: 5,
      debug: true,
    })

    this.connections.set(connectionId, websocket)

    return websocket.connect()
  }

  public async connectToTimer(jwtToken: string, roomId: string): Promise<void> {
    const connectionId = 'timer'

    // Reuse an already-open timer socket instead of tearing it down and
    // recreating it. The status flips via onOpen's setTimerConnectionStatus, so
    // an idempotent connect avoids dropping a live socket (and the timer_update
    // it carries) when the same connect is dispatched again.
    const existingConnection = this.connections.get(connectionId)
    if (existingConnection) {
      const readyState = existingConnection.getReadyState()
      if (readyState === WebSocketReadyState.OPEN) {
        return Promise.resolve()
      }
      if (readyState === WebSocketReadyState.CONNECTING) {
        return Promise.resolve()
      }
      await existingConnection.disconnect()
      this.connections.delete(connectionId)
    }

    const path = `/${roomId}/${WebSocketEndpoint.TIMER}`

    const params: Record<string, string> = {
      jwt_key: jwtToken,
    }

    const websocket = createWebSocket(path, params, {
      onOpen: () => {
        store.dispatch(setTimerConnectionStatus(true))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onConnected) {
          handler.onConnected()
        }
      },
      onMessage: (event) => this.handleMessage(connectionId, event),
      onError: (error) => {
        console.error('Timer WebSocket error:', error)
        store.dispatch(setTimerConnectionStatus(false))
        store.dispatch(setTimerError('WebSocket error'))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onError) {
          handler.onError(new Error('Timer WebSocket error'))
        }
      },
      onClose: () => {
        store.dispatch(setTimerConnectionStatus(false))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onDisconnected) {
          handler.onDisconnected()
        }
      },
      reconnectInterval: 2000,
      maxReconnectAttempts: 5,
      debug: true,
    })

    this.connections.set(connectionId, websocket)

    return websocket.connect()
  }

  public connectToTestChat(
    jwtToken: string,
    chatbotId: string,
    roomId: string
  ): Promise<void> {
    const connectionId = 'testChat'

    const path = `/${roomId}/${WebSocketEndpoint.TEST_CHAT}`

    const params: Record<string, any> = {
      jwt_key: jwtToken,
      chatbot_id: chatbotId,
    }

    const websocket = createWebSocket(path, params, {
      onOpen: () => {
        store.dispatch(setTestChatConnectionStatus(true))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onConnected) {
          handler.onConnected()
        }
      },
      onMessage: (event) => this.handleMessage(connectionId, event),
      onError: (error) => {
        console.error('Advisor WebSocket error:', error)
        store.dispatch(setTestChatConnectionStatus(false))
        store.dispatch(setTestChatError('WebSocket error'))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onError) {
          handler.onError(new Error('Advisor WebSocket error'))
        }
      },
      onClose: () => {
        store.dispatch(setTestChatConnectionStatus(false))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onDisconnected) {
          handler.onDisconnected()
        }
      },
      reconnectInterval: 2000,
      maxReconnectAttempts: 5,
      debug: true,
    })

    this.connections.set(connectionId, websocket)

    return websocket.connect()
  }

  public connectToAIPlayerTestChat(
    jwtToken: string,
    botId: string,
    roomId: string
  ): Promise<void> {
    const connectionId = 'aiPlayerTestChat'

    const path = `/${roomId}/${WebSocketEndpoint.AI_PLAYER_TEST_CHAT}`

    const params: Record<string, any> = {
      jwt_key: jwtToken,
      bot_id: botId,
    }

    const websocket = createWebSocket(path, params, {
      onOpen: () => {
        store.dispatch(setAIPlayerTestChatConnectionStatus(true))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onConnected) {
          handler.onConnected()
        }
      },
      onMessage: (event) => this.handleMessage(connectionId, event),
      onError: (error) => {
        console.error('AI Player Test Chat WebSocket error:', error)
        store.dispatch(setAIPlayerTestChatConnectionStatus(false))
        store.dispatch(setAIPlayerTestChatError('WebSocket error'))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onError) {
          handler.onError(new Error('AI Player Test Chat WebSocket error'))
        }
      },
      onClose: () => {
        store.dispatch(setAIPlayerTestChatConnectionStatus(false))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onDisconnected) {
          handler.onDisconnected()
        }
      },
      reconnectInterval: 2000,
      maxReconnectAttempts: 5,
      debug: true,
    })

    this.connections.set(connectionId, websocket)

    return websocket.connect()
  }

  public connectToGameAnalysisChat(
    jwtToken: string,
    roomId: string
  ): Promise<void> {
    const connectionId = 'gameAnalysis'

    const path = `/${roomId}/${WebSocketEndpoint.GAME_ANALYSIS}`

    const params: Record<string, any> = {
      jwt_key: jwtToken,
    }

    const websocket = createWebSocket(path, params, {
      onOpen: () => {
        store.dispatch(setGameAnalysisConnectionStatus(true))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onConnected) {
          handler.onConnected()
        }
      },
      onMessage: (event) => this.handleMessage(connectionId, event),
      onError: (error) => {
        console.error('Game Analysis WebSocket error:', error)
        store.dispatch(setGameAnalysisConnectionStatus(false))
        store.dispatch(setGameAnalysisError('WebSocket error'))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onError) {
          handler.onError(new Error('Game Analysis WebSocket error'))
        }
      },
      onClose: () => {
        store.dispatch(setGameAnalysisConnectionStatus(false))
        const handler = this.connectionHandlers.get(connectionId)
        if (handler?.onDisconnected) {
          handler.onDisconnected()
        }
      },
      reconnectInterval: 2000,
      maxReconnectAttempts: 5,
      debug: true,
    })

    this.connections.set(connectionId, websocket)

    return websocket.connect()
  }

  public connectToCharterPrep(
    jwtToken: string,
    roomId: string,
    inviteCode: string,
    teamId?: string
  ): Promise<void> {
    const connectionId = 'charter_prep'

    const existing = this.connections.get(connectionId)
    if (existing?.getReadyState() === WebSocketReadyState.OPEN) {
      return Promise.resolve()
    }

    const path = `/${roomId}/${WebSocketEndpoint.CHARTER_PREP}`
    // The server resolves the team itself; there is none before placement.
    const params: Record<string, string> = {
      jwt_key: jwtToken,
      invite_code: inviteCode,
      ...(teamId ? { team_id: teamId } : {}),
    }

    const websocket = createWebSocket(path, params, {
      onOpen: () => {
        store.dispatch(setCharterPrepConnected(true))
        const handler = this.connectionHandlers.get(connectionId)
        handler?.onConnected?.()
      },
      onMessage: (event) => this.handleMessage(connectionId, event),
      onError: (error) => {
        console.error('Charter Prep WebSocket error:', error)
        store.dispatch(setCharterPrepConnected(false))
        store.dispatch(setCharterPrepError('WebSocket connection error'))
        const handler = this.connectionHandlers.get(connectionId)
        handler?.onError?.(new Error('Charter Prep WebSocket error'))
      },
      onClose: () => {
        store.dispatch(setCharterPrepConnected(false))
        const handler = this.connectionHandlers.get(connectionId)
        handler?.onDisconnected?.()
      },
      reconnectInterval: 2000,
      // Long-lived pre-game session: keep retrying across long outages instead
      // of giving up after ~26s. Each successful reconnect re-hydrates the
      // section progress from the backend's chat_history snapshot.
      maxReconnectAttempts: 1000,
      debug: true,
    })

    this.connections.set(connectionId, websocket)
    return websocket.connect()
  }

  public connectToCharterGroup(
    jwtToken: string,
    roomId: string,
    inviteCode: string,
    teamId: string,
    monitor?: boolean
  ): Promise<void> {
    const connectionId = 'charter_group'

    const existing = this.connections.get(connectionId)
    const readyState = existing?.getReadyState()
    if (
      readyState === WebSocketReadyState.OPEN ||
      readyState === WebSocketReadyState.CONNECTING
    ) {
      return Promise.resolve()
    }

    const path = `/${roomId}/${WebSocketEndpoint.CHARTER_GROUP}`
    const params: Record<string, string> = {
      jwt_key: jwtToken,
      invite_code: inviteCode,
      team_id: teamId,
    }
    if (monitor) {
      params.monitor = 'true'
    }

    const websocket = createWebSocket(path, params, {
      onOpen: () => {
        // Guard: only update Redux if this socket is still the registered one.
        // Under StrictMode (mount→unmount→mount) a superseded socket's late
        // onClose could otherwise clobber the new socket's connected state.
        if (this.connections.get(connectionId) !== websocket) return
        store.dispatch(setCharterGroupConnected(true))
        const handler = this.connectionHandlers.get(connectionId)
        handler?.onConnected?.()
      },
      onMessage: (event) => this.handleMessage(connectionId, event),
      onError: (error) => {
        if (this.connections.get(connectionId) !== websocket) return
        console.error('Charter Group WebSocket error:', error)
        store.dispatch(setCharterGroupConnected(false))
        store.dispatch(setCharterGroupError('WebSocket connection error'))
        const handler = this.connectionHandlers.get(connectionId)
        handler?.onError?.(new Error('Charter Group WebSocket error'))
      },
      onClose: () => {
        if (this.connections.get(connectionId) !== websocket) return
        store.dispatch(setCharterGroupConnected(false))
        const handler = this.connectionHandlers.get(connectionId)
        handler?.onDisconnected?.()
      },
      reconnectInterval: 2000,
      // Long-lived pre-game session: keep retrying across long outages instead
      // of giving up after ~26s. Each successful reconnect re-hydrates the
      // group session state from the backend's chat_history snapshot.
      maxReconnectAttempts: 1000,
      debug: true,
    })

    this.connections.set(connectionId, websocket)
    return websocket.connect()
  }

  public connectToCharterPrepMonitor(
    jwtToken: string,
    roomId: string,
    inviteCode: string,
    teamId: string,
    memberId: string
  ): Promise<void> {
    const connectionId = `charter_prep_monitor_${memberId}`

    const existing = this.connections.get(connectionId)
    const readyState = existing?.getReadyState()
    if (
      readyState === WebSocketReadyState.OPEN ||
      readyState === WebSocketReadyState.CONNECTING
    ) {
      return Promise.resolve()
    }

    const path = `/${roomId}/${WebSocketEndpoint.CHARTER_PREP}`
    const params: Record<string, string> = {
      jwt_key: jwtToken,
      invite_code: inviteCode,
      team_id: teamId,
      monitor: 'true',
      member_id: memberId,
    }

    const processors = buildCharterPrepMonitorProcessors(connectionId)
    this.messageProcessors.set(connectionId, processors)

    const websocket = createWebSocket(path, params, {
      onOpen: () => {
        // Guard: only update Redux if this socket is still the registered one.
        // wsManager.disconnect() synchronously deletes from this.connections,
        // so stale onClose/onOpen from a superseded socket are silently dropped.
        if (this.connections.get(connectionId) === websocket) {
          store.dispatch(setMonitorConnected({ memberId, connected: true }))
        }
      },
      onMessage: (event) => this.handleMessage(connectionId, event),
      onError: () => {
        if (this.connections.get(connectionId) === websocket) {
          store.dispatch(setMonitorConnected({ memberId, connected: false }))
        }
      },
      onClose: () => {
        if (this.connections.get(connectionId) === websocket) {
          store.dispatch(setMonitorConnected({ memberId, connected: false }))
        }
      },
      reconnectInterval: 3000,
      maxReconnectAttempts: 5,
      debug: false,
    })

    this.connections.set(connectionId, websocket)
    return websocket.connect()
  }

  public sendMessage(connectionId: string, message: string | object): boolean {
    const connection = this.connections.get(connectionId)
    if (connection && connection.isConnected()) {
      return connection.send(message)
    }
    console.error(
      `Cannot send message, WebSocket ${connectionId} is not connected`
    )
    return false
  }

  public disconnect(connectionId: string): Promise<void> {
    const connection = this.connections.get(connectionId)
    if (connection) {
      this.connections.delete(connectionId)
      return connection.disconnect()
    }
    return Promise.resolve()
  }

  public disconnectAllAdvisorConnections(): Promise<void[]> {
    const ids = [...this.connections.keys()].filter(
      (id) => id === 'advisor' || id.startsWith('advisor_monitor_'),
    )
    return Promise.all(ids.map((id) => this.disconnect(id)))
  }

  public disconnectAll(): Promise<void[]> {
    const promises: Promise<void>[] = []

    for (const [id, connection] of this.connections.entries()) {
      promises.push(connection.disconnect())
      this.connections.delete(id)
    }

    return Promise.all(promises)
  }

  public registerConnectionHandlers(
    connectionId: string,
    handlers: {
      onConnected: () => void
      onDisconnected: () => void
      onError: (error: Error) => void
    }
  ): void {
    this.connectionHandlers.set(connectionId, handlers)
  }

  public isConnected(connectionId: string): boolean {
    const connection = this.connections.get(connectionId)
    return connection ? connection.isConnected() : false
  }
}

export default WebSocketManager
